import { ButtonItem, CustomRule, Form, FormRef, GroupItem, RequiredRule, SimpleItem } from 'devextreme-react/form'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Popup } from 'devextreme-react'
import { useScreenSize } from '@/themes/media-query'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { PopupRef } from 'devextreme-react/cjs/popup'
import LoadPanel, { LoadPanelRef } from 'devextreme-react/cjs/load-panel'
import { ClickEvent } from 'devextreme/ui/button'
import notify from 'devextreme/ui/notify'
import { DataGridRef } from 'devextreme-react/cjs/data-grid'
import { DateBoxTypes } from 'devextreme-react/cjs/date-box'
import { ValidationCallbackData } from 'devextreme/common'
import { causale_centro_costo, centro_costo } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ArraySourceComposer } from '@/auth/api/connector'
import { LookupTypes } from 'devextreme-react/cjs/lookup'

type InsertHolidaysSelectorProps = {
  popupRef: React.RefObject<PopupRef>
  gridRef?: React.RefObject<DataGridRef>
}

const InsertHolidaysSelector = (props: InsertHolidaysSelectorProps) => {
  const { popupRef, gridRef } = props
  const formRef = useRef<FormRef>(null)
  const currentScreenSize = useScreenSize()
  const { client } = useQsAdminApiManager()
  const loadPanelRef = useRef<LoadPanelRef>(null)
  const qsAdminApi = useQsAdminApiManager.getState().service
  const [centriCosto, setCentriCosto] = useState<centro_costo[]>([])
  const [causaliCentroCosto, setCausaliCentroCosto] = useState<causale_centro_costo[]>([])

  const now = new Date()
  now.setHours(13)
  now.setMinutes(0)
  now.setSeconds(0)
  now.setMilliseconds(0)

  const [formData, setFormData] = useState({
    startDate: now,
    endDate: now,
    costCenter: null as centro_costo | null | undefined,
    causalCostCenter: null as causale_centro_costo | null | undefined,
    insertCalendarEvent: false,
    note: null as string | null,
  })

  const getCentriCosto = useCallback(async () => {
    const centriCostoData = await qsAdminApi.centro_costo().query((builder, centroCosto) => {
      builder.filter(centroCosto.visibility.any((ccVisibility) => ccVisibility.reparto.props.id.eq(null)))
      builder.filter(centroCosto.causali_centro_costo.any())
      builder.orderBy(centroCosto.nome.asc())
      builder.select('id', 'nome')
    })
    return centriCostoData.data.value
  }, [qsAdminApi])

  const getCausali = useCallback(
    async (centroCostoId: number) => {
      const causaliData = await qsAdminApi.causale_centro_costo().query((builder, causale) => {
        builder.filter(causale.centro_costo.props.id.eq(centroCostoId))
        builder.orderBy(causale.nome.asc())
        builder.select('id', 'nome', 'note')
      })
      return causaliData.data.value
    },
    [qsAdminApi],
  )

  useEffect(() => {
    getCentriCosto().then((res) => {
      setCentriCosto(res)
      setFormData((prev) =>
        prev
          ? {
              ...prev,
              costCenter: res.find((value) => value.nome === 'FERIE'),
            }
          : prev,
      )
    })
  }, [getCentriCosto])

  return (
    <>
      <Popup
        width={currentScreenSize.isLarge || currentScreenSize.isMedium ? 500 : currentScreenSize.isXSmall ? 350 : 700}
        height={currentScreenSize.isSmall ? 320 : currentScreenSize.isXSmall ? 720 : 'auto'}
        hideOnOutsideClick={true}
        title="Inserisci giornate"
        showCloseButton={true}
        deferRendering={true}
        ref={popupRef}
        resizeEnabled={false}
      >
        <div id="enter-holidays-form-container" className={'dx-card responsive-paddings'}>
          <LoadPanel
            ref={loadPanelRef}
            shadingColor="rgba(0,0,0,0.4)"
            showIndicator={true}
            shading={true}
            showPane={true}
            hideOnOutsideClick={false}
            hideOnParentScroll={false}
            position={{ of: '#enter-holidays-form-container' }}
          />
          <Form
            id={`enter-holidays-form`}
            key={`enter-holidays-form`}
            formData={formData}
            ref={formRef}
            validationGroup="enterHolidaysValidation"
          >
            <GroupItem colCount={2}>
              <SimpleItem
                dataField="startDate"
                editorOptions={{
                  displayFormat: 'dd/MM/yyyy',
                  type: 'date',
                  pickerType: 'calendar',
                  applyValueMode: 'instantly',
                  onValueChanged: (e: DateBoxTypes.ValueChangedEvent) => {
                    console.log('startDate', e)
                    const startDate = new Date(e.value)
                    formRef.current?.instance().updateData('endDate', startDate)
                  },
                }}
                editorType="dxDateBox"
                label={{ text: 'Data Inizio' }}
              >
                <RequiredRule message="Data di inizio obbligatoria" />
              </SimpleItem>
              <SimpleItem
                dataField="endDate"
                editorOptions={{
                  displayFormat: 'dd/MM/yyyy',
                  type: 'date',
                  pickerType: 'calendar',
                  applyValueMode: 'instantly',
                }}
                editorType="dxDateBox"
                label={{ text: 'Data Fine' }}
              >
                <RequiredRule message="Data di fine obbligatoria" />
                <CustomRule
                  reevaluate={true}
                  message="Data fine deve essere uguale o maggiore alla data di inizio"
                  validationCallback={({ value }: ValidationCallbackData) => {
                    const startDate: Date = formData.startDate
                    const endDate: Date = value
                    return endDate >= startDate
                  }}
                />
              </SimpleItem>
              <SimpleItem
                dataField="costCenter"
                label={{ text: 'Centro di Costo' }}
                editorType="dxLookup"
                editorOptions={{
                  dataSource: ArraySourceComposer('id', centriCosto),
                  placeholder: 'Seleziona centro di costo...',
                  displayExpr: 'nome',
                  searchEnabled: true,
                  dropDownCentered: true,
                  dropDownOptions: {
                    showTitle: true,
                    title: 'Selezionare il centro di costo',
                    hideOnOutsideClick: true,
                  },
                  onValueChanged: (e: LookupTypes.ValueChangedEvent) => {
                    if (e.value) {
                      getCausali(e.value.id).then((values: causale_centro_costo[]) => {
                        setCausaliCentroCosto(values)
                        if (values.length === 1) formRef.current?.instance().updateData('causalCostCenter', values[0])
                        else formRef.current?.instance().updateData('causalCostCenter', null)
                      })
                      // if (e.value.nome !== 'FERIE' && e.value.nome !== 'PERMESSO')
                      //   formRef.current?.instance().updateData('insertCalendarEvent', false)
                    }
                  },
                }}
              >
                <RequiredRule message="Centro di costo obbligatorio"></RequiredRule>
              </SimpleItem>
              <SimpleItem
                dataField="causalCostCenter"
                label={{ text: 'Causale' }}
                editorType="dxLookup"
                editorOptions={{
                  dataSource: ArraySourceComposer('id', causaliCentroCosto),
                  placeholder: 'Seleziona la causale...',
                  displayExpr: (item: causale_centro_costo) => {
                    return item ? `${item.nome} - ${item.note}` : ''
                  },
                  searchEnabled: true,
                  dropDownCentered: true,
                  showClearButton: true,
                  dropDownOptions: {
                    showTitle: true,
                    title: 'Selezionare la causale',
                    hideOnOutsideClick: true,
                  },
                }}
              >
                <RequiredRule message="Causale obbligatoria"></RequiredRule>
              </SimpleItem>
              <SimpleItem
                dataField="insertCalendarEvent"
                label={{ text: 'Aggiungi evento su calendario' }}
                editorType="dxSwitch"
                // editorOptions={{
                //   disabled: formData.costCenter?.nome !== 'FERIE' && formData.costCenter?.nome !== 'PERMESSO',
                // }}
              />
            </GroupItem>
            <GroupItem cssClass="last-group">
              <GroupItem cssClass="buttons-group" colCount={1}>
                <ButtonItem
                  name="btn-compute"
                  buttonOptions={{
                    icon: 'fas fa-building',
                    text: 'Inserisci',
                    width: '150px',
                    onClick: async (e: ClickEvent) => {
                      const validationResult = formRef.current?.instance().validate()
                      if (!validationResult?.isValid) return
                      loadPanelRef.current?.instance().option('visible', true)
                      try {
                        const response = await client.post('/api/attivitalavorativa/insertdays', formData, {
                          headers: {
                            'Content-Type': 'application/json',
                          },
                        })
                        if (gridRef) {
                          gridRef.current?.instance().clearSelection()
                          gridRef.current?.instance().refresh()
                        }
                        notify(`Inserimento ferie avvenuto correttamente.`, 'success', 3000)
                      } catch (error: unknown) {
                        notify(`Errore inserimento ferie. Dettagli: ${error}`, 'error', 3000)
                      } finally {
                        loadPanelRef.current?.instance().option('visible', false)
                        popupRef.current?.instance().hide()
                      }
                    },
                  }}
                ></ButtonItem>
              </GroupItem>
            </GroupItem>
          </Form>
        </div>
      </Popup>
    </>
  )
}

export default InsertHolidaysSelector
